import { D, money } from '@makila/fiscal';
import { nextEvent } from './outbox.js';
/**
 * Moda (docs/14 Fase 4): matriz de variantes. O modelo tem uma grelha de
 * tamanhos e de cores; cada combinação é um artigo (variante) com código,
 * preço e stock próprios. Na caixa escolhe-se a cor e o tamanho em dois toques.
 */
/** Grelhas de tamanhos habituais (a gestão escolhe uma e acerta-a). */
export const SIZE_GRIDS = [
    { key: 'letters', label: 'Roupa (XS–XXL)', sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
    { key: 'numbers', label: 'Roupa em números (34–46)', sizes: ['34', '36', '38', '40', '42', '44', '46'] },
    { key: 'shoes', label: 'Calçado (36–45)', sizes: ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45'] },
    { key: 'kids', label: 'Criança (2–14 anos)', sizes: ['2', '4', '6', '8', '10', '12', '14'] },
    { key: 'one', label: 'Tamanho único', sizes: ['Único'] },
];
export const MAX_AXIS = 30;
export const MAX_CELLS = 300;
/** Limpa uma lista da grelha: sem vazios nem repetidos (sem olhar a maiúsculas), pela ordem dada. */
export function cleanAxis(values, max, what) {
    const seen = new Set();
    const out = [];
    for (const v of values) {
        const x = v.trim().replace(/\s+/g, ' ');
        if (!x)
            continue;
        if (x.length > max)
            throw new RangeError(`${what} demasiado comprido: "${x}" (máximo ${max} caracteres)`);
        const k = x.toLocaleLowerCase('pt');
        if (seen.has(k))
            continue;
        seen.add(k);
        out.push(x);
    }
    if (out.length > MAX_AXIS)
        throw new RangeError(`No máximo ${MAX_AXIS} ${what.toLowerCase()}s`);
    return out;
}
/** Todas as combinações da grelha (só tamanhos, só cores, ou tamanho × cor). */
export function matrixCells(sizes, colors) {
    const s = sizes.length ? sizes : [null];
    const c = colors.length ? colors : [null];
    if (!sizes.length && !colors.length)
        return [];
    const cells = c.flatMap((color) => s.map((size) => ({ size, color })));
    if (cells.length > MAX_CELLS)
        throw new RangeError(`A grelha dá ${cells.length} combinações: o máximo é ${MAX_CELLS}`);
    return cells;
}
/** Nome da variante: "M / Azul". */
export const cellLabel = (c) => [c.size, c.color].filter(Boolean).join(' / ');
const slug = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '').toUpperCase();
/** Código da variante a partir do do modelo: CAM01-M-AZUL (até 40 caracteres). */
export function cellCode(modelCode, c) {
    const parts = [c.size && slug(c.size), c.color && slug(c.color).slice(0, 8)].filter(Boolean);
    return [modelCode.slice(0, 40 - parts.join('-').length - 1), ...parts].join('-').slice(0, 40);
}
const same = (a, b) => (a ?? '').toLocaleLowerCase('pt') === (b ?? '').toLocaleLowerCase('pt');
export const sameCell = (a, b) => same(a.size, b.size) && same(a.color, b.color);
/**
 * O que falta criar e o que se desactiva ou reactiva quando a grelha muda.
 * As variantes não se apagam (têm vendas e stock): saem da grelha inactivas.
 */
export function planMatrix(sizes, colors, existing) {
    const cells = matrixCells(sizes, colors);
    const inGrid = (v) => cells.some((c) => sameCell(c, v));
    return {
        create: cells.filter((c) => !existing.some((v) => sameCell(c, v))),
        deactivate: existing.filter((v) => v.active && !inGrid(v)).map((v) => v.id),
        reactivate: existing.filter((v) => !v.active && inGrid(v)).map((v) => v.id),
    };
}
/**
 * Grelha da caixa: as cores e os tamanhos do modelo (pela ordem da gestão),
 * só com os que têm alguma variante activa. Variantes antigas, sem tamanho nem
 * cor, ficam numa lista à parte.
 */
export function variantGrid(model, variants) {
    const live = variants.filter((v) => v.active);
    const gridded = live.filter((v) => v.variantSize || v.variantColor);
    const order = (axis, pick) => {
        const used = [...new Set(gridded.map(pick).filter((x) => !!x))];
        const known = (axis ?? []).filter((x) => used.some((u) => same(u, x)));
        return [...known, ...used.filter((u) => !known.some((k) => same(k, u)))];
    };
    const sizes = order(model.variantSizes, (v) => v.variantSize);
    const colors = order(model.variantColors, (v) => v.variantColor);
    return {
        sizes, colors,
        cell: (size, color) => gridded.find((v) => same(v.variantSize, size) && same(v.variantColor, color)),
        others: live.filter((v) => !v.variantSize && !v.variantColor),
    };
}
/**
 * Pedido de uma peça a outra loja (a caixa viu-a no stock de lá): chega à
 * gestão, que a envia por transferência.
 */
export async function requestStock(store, who, r) {
    const q = Number(r.quantity);
    if (!(q > 0) || q > 1000)
        throw new RangeError('Quantidade inválida');
    const now = r.now ?? new Date();
    return store.transaction(async (tx) => {
        const ev = nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'stock.requested', now,
            payload: { fromStoreId: r.fromStoreId, productId: r.productId, quantity: r.quantity, note: r.note?.trim().slice(0, 200) || null, operatorId: r.operatorId, at: now.toISOString() } });
        await tx.appendOutbox(ev);
        return { id: ev.id };
    });
}
/**
 * Consignação: a parte da loja (percentagem sobre o valor sem IVA, ao cêntimo)
 * e o que se paga ao fornecedor. O IVA da venda é da loja (vende em nome
 * próprio); o fornecedor factura a loja pelo que lhe cabe.
 */
export function consignmentSplit(net, pct) {
    const n = new D(net);
    const share = n.times(pct).div(100).toDecimalPlaces(2);
    return { storeShare: money(share), due: money(n.minus(share)) };
}
//# sourceMappingURL=fashion.js.map